import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { useActiveOrganization } from "@/hooks/organizations";
import { Pagination } from "@/types";
import { OrganizationMember } from "@/types/organization-member";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const useActiveOrgMembers = () => {
  const activeOrg = useActiveOrganization();
  const userToken = useUserToken();
  const url = new URL(ENDPOINTS.organization_members);
  if (activeOrg)
    url.searchParams.append("organization_id", activeOrg.id.toString());
  return useQuery(
    [ENDPOINTS.organization_members],
    (): Promise<Pagination<OrganizationMember>> =>
      fetch(url, {
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
  );
};

const useDeleteOrgMember = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  return useMutation(
    (organizationMemberId: number) =>
      fetch(`${ENDPOINTS.organization_members}/${organizationMemberId}`, {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      }),
    {
      onSuccess: async (response) => {
        if (response.ok) {
          queryClient.invalidateQueries([ENDPOINTS.organization_members]);
          displayToast("Member removed successfully", ToastType.success);
        } else {
          const data = await response.json();
          displayErrorToast(data.message);
        }
      },
    },
  );
};

const usePatchOrgMember = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();
  return useMutation(
    ({
      organizationMemberId,
      organizationRoleId,
    }: {
      organizationMemberId: number;
      organizationRoleId: number;
    }) =>
      fetch(`${ENDPOINTS.organization_members}/${organizationMemberId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ organization_role_id: organizationRoleId }),
      }),
    {
      onSuccess: async (response) => {
        if (response.ok) {
          queryClient.invalidateQueries([ENDPOINTS.organization_members]);
          queryClient.invalidateQueries([ENDPOINTS.legacy_workspace_members]);
          displayToast("Member role updated successfully", ToastType.success);
        } else {
          const data = await response.json();
          displayErrorToast(data.message);
        }
      },
    },
  );
};

export { useActiveOrgMembers, useDeleteOrgMember, usePatchOrgMember };
